"use client"

import { useAuth } from "@clerk/nextjs"
import { useCallback, useEffect, useMemo, useState } from "react"

import type { UserEventListItem } from "@/features/events/user-event-collection"
import {
  fetchMyRelatedEventsFromApi,
  userEventListItemsFromMyRelatedDtos,
} from "@/lib/data/events"
import type { MyRelatedEventListDto } from "@/lib/events/schemas"
import type { TimelineSave } from "@/lib/timeline-store"

type Options = {
  timelineSaves: TimelineSave[]
  timelineLoaded: boolean
}

/**
 * Unified, deduped event list from `GET /api/events/my` only.
 * Refetches when timeline save ids change so `isSaved` stays aligned with `/api/timeline`.
 */
export function useUserEventCollection(opts: Options) {
  const { userId, isLoaded: authLoaded } = useAuth()
  const [serverDtos, setServerDtos] = useState<MyRelatedEventListDto[]>([])
  const [apiLoaded, setApiLoaded] = useState(false)

  const saveKey = useMemo(
    () =>
      [...opts.timelineSaves.map((s) => s.eventId)].sort().join("\u0001"),
    [opts.timelineSaves],
  )

  const loadMy = useCallback(async () => {
    if (!userId?.trim()) {
      setServerDtos([])
      setApiLoaded(true)
      return
    }
    try {
      const rows = await fetchMyRelatedEventsFromApi()
      setServerDtos(rows)
    } catch {
      setServerDtos([])
    } finally {
      setApiLoaded(true)
    }
  }, [userId])

  useEffect(() => {
    void loadMy()
  }, [loadMy, saveKey])

  const items = useMemo((): UserEventListItem[] => {
    if (!opts.timelineLoaded || !authLoaded) return []
    return userEventListItemsFromMyRelatedDtos(serverDtos)
  }, [serverDtos, opts.timelineLoaded, authLoaded])

  const isLoaded =
    authLoaded &&
    opts.timelineLoaded &&
    (!userId?.trim() || apiLoaded)

  return { items, isLoaded, refresh: loadMy }
}
